import { useCallback, useEffect, useState } from "react";
import {
  atClient,
  centsFromInput,
  formatMoney,
  MOVEMENT_LABELS,
  reserveTagLine,
  RETIRE_REASON_LABELS,
  SERIAL_FIELD_LABEL,
  STOCK_STATE_LABELS,
  todayLocal,
  TRACKING_LABELS,
  type AttachmentMeta,
  type Client,
  type Deployment,
  type Item,
  type ItemHold,
  type OpenDeployment,
  type StockDetail,
  type StockRow,
  type StockTracking,
  formatCount,
} from "@iltocat/shared";
import { ApiError, api, fetchAttachment } from "../lib/api.js";
import { downloadBlob } from "../lib/download.js";
import { calendarDay, when } from "../lib/stockFormat.js";
import { Icon } from "./Icon.js";
import { Notice } from "./Notice.js";
import { Panel } from "./Panel.js";
import { Segmented } from "@vcnet/ui";
import {
  ConvertToBulkPanel,
  ConvertToSerializedPanel,
  DeployPanel,
  RetirePanel,
  ReturnPanel,
  TransferPanel,
} from "./StockForms.js";
import { useToast } from "./Toast.js";

type Action =
  | { kind: "deploy"; clients: Client[]; markup: number }
  | { kind: "retire" }
  | { kind: "to-bulk" }
  | { kind: "to-serialized" }
  | { kind: "return"; line: OpenDeployment }
  | { kind: "move"; line: OpenDeployment; clients: Client[] };

/**
 * An item's life as stock, on its own screen: where it is, what it cost, the
 * receipt it came in on, every time it went out or came back -- and the
 * buttons that move it, which open the same panels the Stock screen uses.
 *
 * An item captured by hand in a stockroom is not stock until somebody says
 * so, and this is where they say it ("Put on the shelf"). Before, the only
 * way onto the shelf was a receipt, so the Items screen and the shelf felt
 * like two lists that happened to share some rows.
 *
 * The cost and the vendor's invoice number are shown here, marked private:
 * this is the inventory's own member looking at their own books. Neither is
 * on `Item`, so neither can reach the listing or the prompt screens that
 * read it.
 */
/**
 * The item's stock record and the action being taken on it, held above the
 * panel so the item screen's header can offer the same Deploy or Return
 * button the panel does.
 */
export function useStockItem(item: Item | null, enabled: boolean) {
  const [detail, setDetail] = useState<StockDetail | null | "none">(null);
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] = useState<Action | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const itemId = item?.id;
  const updatedAt = item?.updatedAt;

  const load = useCallback(() => {
    if (!enabled || !itemId) return;
    void api.stock
      .detail(itemId)
      .then((found) => setDetail(found))
      .catch((problem: unknown) => {
        // A 404 means an item captured in a stock inventory but never put on
        // the shelf: nothing is wrong, it is just not stock yet.
        if (problem instanceof ApiError && problem.status === 404) setDetail("none");
        else setError(problem instanceof ApiError ? problem.message : "Could not load its stock history.");
      });
  }, [enabled, itemId]);

  useEffect(() => load(), [load, updatedAt]);

  // The standard markup, for reading the item's price against its cost. Quiet
  // on failure: the price link is a convenience, not the screen.
  const [markup, setMarkup] = useState<number | null>(null);
  useEffect(() => {
    if (!enabled) return;
    void api.stock
      .settings()
      .then(({ settings }) => setMarkup(settings.markupPercent))
      .catch(() => undefined);
  }, [enabled]);

  const open =
    detail && detail !== "none" ? detail.deployments.filter((line) => line.quantity > line.returned) : [];
  const stockRecord = detail && detail !== "none" ? detail.stock : null;
  // Every deployment wrote a movement carrying its client's name, so the
  // history already knows it; no second request for the client list.
  const clientName = (clientId: string) =>
    detail && detail !== "none"
      ? (detail.movements.find((movement) => movement.clientId === clientId)?.clientName ?? "")
      : "";
  const canDeploy = stockRecord !== null && stockRecord.state === "in_stock" && stockRecord.onHand > 0;

  async function startDeploy() {
    setActionError(null);
    try {
      const [{ clients }, { settings }] = await Promise.all([api.clients.list(), api.stock.settings()]);
      setAction({ kind: "deploy", clients, markup: settings.markupPercent });
    } catch (problem) {
      setActionError(problem instanceof ApiError ? problem.message : "Could not load the client list.");
    }
  }

  /** A deployment as the shared panels want it: with the item's title, serial and the client's name. */
  const openLine = (line: Deployment): OpenDeployment | null =>
    item && stockRecord
      ? {
          ...line,
          title: item.title,
          serial: serialOf(item),
          clientName: clientName(line.clientId),
          tracking: stockRecord.tracking,
        }
      : null;

  function startReturn(line: Deployment) {
    const full = openLine(line);
    if (full) setAction({ kind: "return", line: full });
  }

  async function startMove(line: Deployment) {
    const full = openLine(line);
    if (!full) return;
    setActionError(null);
    try {
      const { clients } = await api.clients.list();
      setAction({ kind: "move", line: full, clients });
    } catch (problem) {
      setActionError(problem instanceof ApiError ? problem.message : "Could not load the client list.");
    }
  }

  /** What the header's one stock button does: put it out, or take it back. */
  const primary: { label: "Deploy" | "Return"; run: () => void } | null = canDeploy
    ? { label: "Deploy", run: () => void startDeploy() }
    : open[0]
      ? { label: "Return", run: () => startReturn(open[0]!) }
      : null;

  return {
    detail,
    markup,
    setDetail,
    error,
    action,
    setAction,
    actionError,
    load,
    open,
    clientName,
    canDeploy,
    startDeploy,
    startReturn,
    startMove,
    primary,
  };
}

export type StockItem = ReturnType<typeof useStockItem>;

export function StockPanel({
  item,
  model,
  onChanged,
}: {
  item: Item;
  model: StockItem;
  onChanged: () => void;
}) {
  const { detail, setDetail, error, action, setAction, actionError, load, open, clientName, canDeploy } =
    model;

  if (error) return <Notice tone="error">{error}</Notice>;
  if (detail === null) return <div className="skeleton-panel" />;
  if (detail === "none") {
    return (
      <ShelvePanel
        item={item}
        onDone={(next) => {
          setDetail(next);
          onChanged();
        }}
      />
    );
  }

  const { stock, receipt, movements, retirements } = detail;
  const retirement = retirements[0];
  const serial = serialOf(item);
  const row: StockRow = {
    ...stock,
    title: item.title,
    serial,
    categoryId: item.categoryId,
    coverPhotoId: item.photos[0]?.id ?? null,
    priceCents: item.priceCents,
    holds: detail.holds.map((hold) => ({
      reserveId: hold.reserveId,
      clientId: hold.clientId,
      clientName: hold.clientName,
      quantity: hold.open,
    })),
  };
  const done = () => {
    setAction(null);
    load();
    onChanged();
  };

  const canRetire = stock.state !== "retired" && (stock.tracking === "serialized" || stock.onHand > 0);

  return (
    <>
      <Panel
        title="Stock"
        note="Cost and the vendor's invoice number stay private: they never reach a listing or a prompt."
        actions={
          action === null && (
            <>
              {canDeploy && (
                <button
                  type="button"
                  className="button button-small"
                  onClick={() => void model.startDeploy()}
                >
                  <Icon name="external" size={16} />
                  Deploy…
                </button>
              )}
              {open.map((line) => (
                <button
                  key={line.id}
                  type="button"
                  className="button button-ghost button-small"
                  onClick={() => model.startReturn(line)}
                >
                  Return{open.length > 1 ? ` from ${clientName(line.clientId)}` : ""}
                </button>
              ))}
              {open.map((line) => (
                <button
                  key={`move-${line.id}`}
                  type="button"
                  className="button button-ghost button-small"
                  onClick={() => void model.startMove(line)}
                >
                  Move{open.length > 1 ? ` from ${clientName(line.clientId)}` : ""} to…
                </button>
              ))}
              {canRetire && (
                <button
                  type="button"
                  className="button button-ghost button-small"
                  onClick={() => setAction({ kind: "retire" })}
                >
                  Retire
                </button>
              )}
            </>
          )
        }
      >
        {actionError && <Notice tone="error">{actionError}</Notice>}
        <dl className="facts">
          <div>
            <dt>Where it is</dt>
            <dd>
              <span className={`pill stock-state stock-state-${stock.state}`}>
                {STOCK_STATE_LABELS[stock.state]}
              </span>
              {stock.clientId && (
                <span className="muted">
                  {" "}
                  at{" "}
                  {atClient(
                    clientName(stock.clientId),
                    open.find((line) => line.clientId === stock.clientId)?.spot ?? "",
                  )}
                </span>
              )}
            </dd>
          </div>
          <div>
            <dt>Tracked as</dt>
            <dd>
              {TRACKING_LABELS[stock.tracking]}
              {stock.state !== "retired" && action === null && (
                <>
                  {" "}
                  {stock.tracking === "bulk" && (
                    <>
                      <button
                        type="button"
                        className="link"
                        onClick={() => setAction({ kind: "to-serialized" })}
                      >
                        Convert to serialized…
                      </button>
                      {" · "}
                    </>
                  )}
                  <button type="button" className="link" onClick={() => setAction({ kind: "to-bulk" })}>
                    {stock.tracking === "serialized" ? "Convert to bulk…" : "Merge other stock into it…"}
                  </button>
                </>
              )}
            </dd>
          </div>
          {stock.tracking === "bulk" && (
            <div>
              <dt>On the shelf / out</dt>
              <dd>
                {formatCount(stock.onHand)} / {formatCount(stock.deployed)}
              </dd>
            </div>
          )}
          <div>
            <dt>
              <Icon name="lock" size={12} /> Unit cost
            </dt>
            <dd>{stock.unitCostCents === null ? "Not given" : formatMoney(stock.unitCostCents)}</dd>
          </div>
          {receipt && (
            <div>
              <dt>
                <Icon name="lock" size={12} /> Received
              </dt>
              <dd>
                {calendarDay(receipt.receivedOn)}
                {receipt.vendor && ` from ${receipt.vendor}`}
                {receipt.invoiceNumber && `, invoice ${receipt.invoiceNumber}`}
                {receipt.attachment && <AttachmentLink attachment={receipt.attachment} />}
              </dd>
            </div>
          )}
          {retirement && (
            <div>
              <dt>Retired</dt>
              <dd>
                {RETIRE_REASON_LABELS[retirement.reason]}, {calendarDay(retirement.retiredOn)}
                {retirement.disposalVendor && ` via ${retirement.disposalVendor}`}
                {retirement.note && ` (${retirement.note})`}
                {retirement.certificate && <AttachmentLink attachment={retirement.certificate} />}
              </dd>
            </div>
          )}
        </dl>

        {stock.state !== "retired" && (
          <ReservesSection
            item={item}
            detail={detail}
            onChanged={() => {
              load();
              onChanged();
            }}
          />
        )}

        <h3 className="facts-title">History</h3>
        <table className="table">
          <thead>
            <tr>
              <th>When</th>
              <th>What</th>
              <th>Units</th>
              <th>Where</th>
              <th>Price</th>
              <th>Who</th>
            </tr>
          </thead>
          <tbody>
            {movements.map((movement) => (
              <tr key={movement.id}>
                <td data-label="When" title={`Entered ${when(movement.at)}`}>
                  {calendarDay(movement.occurredOn)}
                </td>
                <td data-label="What">
                  <span>
                    {MOVEMENT_LABELS[movement.kind]}
                    {movement.note && <span className="muted"> · {movement.note}</span>}
                  </span>
                </td>
                <td data-label="Units">{movement.quantity}</td>
                <td data-label="Where">{movement.clientName || "Stockroom"}</td>
                <td data-label="Price">
                  {movement.priceCents === null ? "" : formatMoney(movement.priceCents)}
                </td>
                <td data-label="Who" className="muted">
                  {movement.actor}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      {action?.kind === "deploy" && (
        <DeployPanel
          rows={[row]}
          clients={action.clients}
          markup={action.markup}
          onClients={(clients) => setAction({ ...action, clients })}
          onCancel={() => setAction(null)}
          onDone={done}
        />
      )}
      {action?.kind === "return" && (
        <ReturnPanel lines={[action.line]} onCancel={() => setAction(null)} onDone={done} />
      )}
      {action?.kind === "move" && (
        <TransferPanel
          lines={[action.line]}
          clients={action.clients}
          onClients={(clients) => setAction({ ...action, clients })}
          onCancel={() => setAction(null)}
          onDone={done}
        />
      )}
      {action?.kind === "retire" && (
        <RetirePanel rows={[row]} onCancel={() => setAction(null)} onDone={done} />
      )}
      {action?.kind === "to-bulk" && (
        <ConvertToBulkPanel
          rows={[row]}
          sameName={row.tracking === "serialized"}
          onCancel={() => setAction(null)}
          onDone={done}
        />
      )}
      {action?.kind === "to-serialized" && (
        <ConvertToSerializedPanel row={row} onCancel={() => setAction(null)} onDone={done} />
      )}
    </>
  );
}

/**
 * Back stock, from the item's side: which reserves it is held for (with a
 * release), and which client reserves its tags match that it could be pulled
 * into. An item that matches nothing and is held for nobody says so in one
 * line, which is the prompt to tag it.
 */
function ReservesSection({
  item,
  detail,
  onChanged,
}: {
  item: Item;
  detail: StockDetail;
  onChanged: () => void;
}) {
  const { stock, holds, reserves } = detail;
  const heldHere = holds.reduce((sum, hold) => sum + hold.open, 0);
  const free = Math.max(stock.onHand - heldHere, 0);
  const [count, setCount] = useState("1");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const pullable = reserves.filter(
    (reserve) => !holds.some((hold) => hold.reserveId === reserve.id) || stock.tracking === "bulk",
  );

  async function pull(reserveId: string, clientName: string) {
    const quantity = stock.tracking === "bulk" ? Number(count) : 1;
    if (!Number.isInteger(quantity) || quantity < 1 || quantity > free) {
      return setError(`Hold between 1 and ${free}.`);
    }
    setBusy(true);
    setError(null);
    try {
      await api.reserves.hold(reserveId, { heldOn: todayLocal(), lines: [{ itemId: item.id, quantity }] });
      toast(`Held for ${clientName}.`);
      onChanged();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "That did not hold.");
    } finally {
      setBusy(false);
    }
  }

  async function release(hold: ItemHold) {
    setBusy(true);
    setError(null);
    try {
      await api.reserves.release(hold.id, { quantity: hold.open, releasedOn: todayLocal() });
      toast("Released: free for anybody.");
      onChanged();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "That did not release.");
    } finally {
      setBusy(false);
    }
  }

  if (holds.length === 0 && reserves.length === 0) {
    return (
      <p className="muted small item-reserves-none">
        <Icon name="bookmark" size={14} /> No client reserve matches this item’s tags.
      </p>
    );
  }

  return (
    <div className="item-reserves">
      <h3 className="facts-title">Reserves</h3>
      {holds.length > 0 && (
        <ul className="item-holds">
          {holds.map((hold) => (
            <li key={hold.id} className="item-hold">
              <span className="held-badge">
                <Icon name="bookmark" size={12} />
                {stock.tracking === "bulk" ? `${hold.open} held` : "Held"} for {hold.clientName}
              </span>
              <span className="muted small">
                {reserveTagLine(hold.tags)}
                {hold.billing === "bill" &&
                  (hold.billedAt ? " · paid in advance, billed" : " · paid in advance, waiting to bill")}
                {hold.billing === "credit" && " · already paid for"}
              </span>
              <button
                type="button"
                className="button button-ghost button-small"
                disabled={busy}
                onClick={() => void release(hold)}
              >
                Release
              </button>
            </li>
          ))}
        </ul>
      )}
      {free > 0 && pullable.length > 0 && (
        <div className="item-pull">
          <span className="muted small">
            {stock.tracking === "bulk" ? `${free} free on the shelf. Hold` : "Free on the shelf. Hold it"}{" "}
            for:
          </span>
          {stock.tracking === "bulk" && (
            <input
              className="input input-small input-number"
              type="number"
              min={1}
              max={free}
              aria-label="Units to hold"
              value={count}
              onChange={(event) => setCount(event.target.value)}
            />
          )}
          {pullable.map((reserve) => (
            <button
              key={reserve.id}
              type="button"
              className={reserve.status === "ok" ? "chip" : "chip chip-want"}
              disabled={busy}
              title={`${reserveTagLine(reserve.tags)} · ${reserve.count} of ${reserve.minimum} held${reserve.prepaid ? " · paid in advance" : ""}`}
              onClick={() => void pull(reserve.id, reserve.clientName)}
            >
              <Icon name="bookmark" size={12} />
              {reserve.clientName}
              <span className="chip-count">
                {reserve.count}/{reserve.minimum}
              </span>
            </button>
          ))}
        </div>
      )}
      {error && <Notice tone="error">{error}</Notice>}
    </div>
  );
}

/** The item's serial, where receiving puts it: the first field with the serial's label. */
const serialOf = (item: Item): string =>
  item.fields.find((field) => field.label.toLowerCase() === SERIAL_FIELD_LABEL.toLowerCase())?.value ?? "";

/**
 * An item in the stockroom's list but not in its stock. Serialized is the
 * default when the item already has a serial, bulk otherwise.
 */
function ShelvePanel({ item, onDone }: { item: Item; onDone: (detail: StockDetail) => void }) {
  const [tracking, setTracking] = useState<StockTracking>(serialOf(item) ? "serialized" : "bulk");
  const [quantity, setQuantity] = useState("1");
  const [cost, setCost] = useState("");
  const [receivedOn, setReceivedOn] = useState(todayLocal());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    const count = tracking === "bulk" ? Number(quantity) : 1;
    if (!Number.isInteger(count) || count < 1) return setError("Give a count of 1 or more.");
    const unitCostCents = cost.trim() ? centsFromInput(cost) : null;
    if (cost.trim() && unitCostCents === null) return setError("That cost is not a number.");
    setBusy(true);
    setError(null);
    try {
      const detail = await api.stock.shelve(item.id, {
        tracking,
        quantity: count,
        unitCostCents,
        receivedOn,
      });
      toast("On the shelf.");
      onDone(detail);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "That did not go through.");
      setBusy(false);
    }
  }

  return (
    <Panel
      title="Not on the shelf yet"
      note="This item is in the stockroom's list but not in its stock. Put it on the shelf to deploy, return and retire it. Stock that arrives on an invoice goes through Receive instead."
    >
      <form className="stack" onSubmit={submit}>
        <Segmented<StockTracking>
          label="How it is tracked"
          size="sm"
          value={tracking}
          onChange={setTracking}
          options={[
            { value: "serialized", label: `${TRACKING_LABELS.serialized}: this one unit` },
            { value: "bulk", label: `${TRACKING_LABELS.bulk}: a count` },
          ]}
        />
        <div className="split">
          {tracking === "bulk" && (
            <label className="field">
              <span className="field-label">How many</span>
              <input
                className="input input-number"
                type="number"
                min={1}
                value={quantity}
                onChange={(event) => setQuantity(event.target.value)}
              />
            </label>
          )}
          <label className="field">
            <span className="field-label">
              <Icon name="lock" size={12} /> Unit cost (optional)
            </span>
            <input
              className="input input-number"
              inputMode="decimal"
              value={cost}
              onChange={(event) => setCost(event.target.value)}
            />
          </label>
          <label className="field">
            <span className="field-label">In stock from</span>
            <input
              className="input"
              type="date"
              required
              value={receivedOn}
              onChange={(event) => setReceivedOn(event.target.value)}
            />
          </label>
        </div>
        {tracking === "serialized" && !serialOf(item) && (
          <p className="muted small">
            Fill in the serial number above, under the title, so search and its label can find it by serial.
          </p>
        )}
        {error && <Notice tone="error">{error}</Notice>}
        <div className="panel-foot">
          <button type="submit" className="button" disabled={busy}>
            <Icon name="box" size={18} />
            {busy ? "Putting it on the shelf…" : "Put on the shelf"}
          </button>
        </div>
      </form>
    </Panel>
  );
}

export function AttachmentLink({ attachment }: { attachment: AttachmentMeta }) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      className="link attachment-link"
      disabled={busy}
      onClick={() => {
        setBusy(true);
        void fetchAttachment(attachment.id)
          .then((blob) => downloadBlob(blob, attachment.name))
          .finally(() => setBusy(false));
      }}
    >
      <Icon name="download" size={14} />
      {attachment.name}
    </button>
  );
}
